import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import formatCurrency from '../helper/formatCurrency';
import Swal from 'sweetalert2';
import { Search, RotateCcw, Calendar, CheckCircle2, XCircle, Clock } from 'lucide-react';

interface BounceRewardItem {
  id: number;
  user_id: number;
  player_name: string;
  referrer_id: number;
  referrer_name: string;
  deposit_id: string;
  deposit_amount: number;
  bonus_percentage: number;
  bonus_amount: number;
  turnover_required: number;
  turnover_current: number;
  status: string;
  created_at: string;
  expiry_at: string;
  reward_type?: string;
}

const BounceReward: React.FC = () => {
  const [rewards, setRewards] = useState<BounceRewardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(15);
  const [totalCount, setTotalCount] = useState(0);

  const fetchRewards = async () => {
    setLoading(true);
    try {
      const res = await api.get('/bonus-rewards', {
        params: {
          page,
          limit,
          search: searchTerm || undefined,
          status: statusFilter || undefined,
        },
      });

      if (res.data.status) {
        setRewards(res.data.data);
        setTotalPages(res.data.pagination.totalPages);
        setTotalCount(res.data.pagination.totalRecords);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch referral bounce rewards.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRewards();
  }, [page, statusFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchRewards();
  };

  const handleReset = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPage(1);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'settled':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Settled
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200">
            <XCircle className="w-3.5 h-3.5" />
            Cancelled
          </span>
        );
      case 'created':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
            <Clock className="w-3.5 h-3.5" />
            Created
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Referral Bounce Rewards</h1>
        <p className="text-sm text-gray-500">
          Track referral rewards based on 30% deposit turnover conditions within 24 hours of player registration.
        </p>
      </div>

      {/* Filters & Actions */}
      <form onSubmit={handleSearchSubmit} className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl border">
        <div className="flex flex-col gap-1 w-64">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Player or Referrer"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 rounded border w-full px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1 w-48">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="rounded border px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="created">Created (Pending)</option>
            <option value="settled">Settled</option>
            <option value="cancelled">Cancelled (Expired)</option>
          </select>
        </div>

        <div className="flex items-end gap-2 h-14 pt-5">
          <button
            type="submit"
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold text-sm transition"
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded font-semibold text-sm transition flex items-center gap-1.5 border"
            title="Reset Filters"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </form>

      {/* Main Table */}
      <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm truncate">
          <thead className="bg-gray-50 border-b">
            <tr>
              {[
                'S.No',
                'Player Name',
                'Referrer Name',
                'Deposit Amount',
                'Bonus (%)',
                'Bonus Amount',
                'Turnover Requirement (30%)',
                'Current Turnover',
                'Status',
                'Created At',
                'Expiry At',
              ].map((h) => (
                <th key={h} className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={11} className="text-center py-8 text-gray-500">
                  Loading referral bounce rewards...
                </td>
              </tr>
            ) : rewards.length === 0 ? (
              <tr>
                <td colSpan={11} className="text-center py-8 text-gray-500">
                  No referral bounce rewards found.
                </td>
              </tr>
            ) : (
              rewards.map((reward, i) => {
                const isDepositBased = !!reward.deposit_id;
                return (
                  <tr key={reward.id} className="hover:bg-gray-50/50 transition">
                    <td className="px-4 py-3 text-gray-900 font-medium">
                      {(page - 1) * limit + i + 1}
                    </td>
                    <td className="px-4 py-3 text-gray-900 font-semibold">
                      {reward.player_name ? `(${reward.user_id}) ${reward.player_name}` : `ID: ${reward.user_id}`}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {reward.reward_type === 'cashback' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                          Cashback (Self)
                        </span>
                      ) : (
                        reward.referrer_name ? `(${reward.referrer_id}) ${reward.referrer_name}` : `ID: ${reward.referrer_id}`
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900">
                      {isDepositBased ? formatCurrency(reward.deposit_amount) : <span className="text-gray-400 italic">N/A (Signup Track)</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {isDepositBased ? `${reward.bonus_percentage}%` : '-'}
                    </td>
                    <td className="px-4 py-3 font-semibold text-blue-600">
                      {isDepositBased ? formatCurrency(reward.bonus_amount) : '-'}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {reward.reward_type === 'cashback' ? (
                        <span className="text-gray-500 italic">Loss of Deposit</span>
                      ) : isDepositBased ? (
                        formatCurrency(reward.turnover_required)
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {reward.reward_type === 'cashback' ? (
                        <span className="text-gray-500 italic">Wallet Bal &lt; 1</span>
                      ) : isDepositBased ? (
                        <div className="flex flex-col gap-1 w-32">
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(reward.turnover_current)}
                          </span>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full ${
                                reward.status === 'settled' ? 'bg-emerald-500' : 'bg-blue-500'
                              }`}
                              style={{
                                width: `${Math.min(
                                  100,
                                  (Number(reward.turnover_current) / Number(reward.turnover_required)) * 100
                                )}%`,
                              }}
                            ></div>
                          </div>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(reward.status)}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {new Date(reward.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {new Date(reward.expiry_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-end gap-2 mt-4">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-4 py-2 border rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition text-sm font-semibold disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-4 py-2 text-sm text-gray-600">
            Page {page} of {totalPages} ({totalCount} total)
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-4 py-2 border rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition text-sm font-semibold disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default BounceReward;
